import { Suspense, useState } from 'react';
import { useQueryState } from 'nuqs';
import {
  Box,
  Grid,
  InlineStack,
  Page,
  PageBody,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useObjectTypes } from '@/services/db/object-type/queries';
import { isMultiIdentifier } from '@/features/people';
import { useBreadcrumbTitle, useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { isEmptyString } from '@/utils/string';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { dataIndexItemPath, type DataIndexItemAddress } from '../urls';
import { rememberedListPath } from '../list-memory';
import { ItemDetails } from './details-panel';
import { EventAttributesTable } from './event-attributes-table';
import { ItemHeader } from './item-header';
import { useDataIndexItem, useItemReach, useItemTags } from './hooks';
import { useItemPageModel } from './page-model';
import { RecentActivity } from './recent-activity';
import { LiquidCaveat } from '../components/liquid-caveat';
import { showsLiquidCaveat } from '../utils/usage-surfaces';
import { isSameItemRef, itemRefFromAddress } from '../utils/item';
import { itemTabs, resolveItemTab } from './tabs';
import { UsageTab } from './usage-tab';

/**
 * One attribute's or event's own page.
 *
 * The detail read is the page: everything on it — what uses the item, what
 * can be done to it, and whether it can be deleted at all — comes out of the
 * same record, so the page suspends on it rather than assembling itself in
 * pieces.
 */

type DataIndexItemPageProps = {
  address: DataIndexItemAddress;
};

export default function DataIndexItemPage({ address }: DataIndexItemPageProps) {
  // The tab title is the crumb chain, leaf first — "city | Data index", or
  // "Companies.tier | Data index" — as the page this replaces builds it
  // (`services/crumbs.ts` `createTitle`). Read back from the crumb store so
  // the two cannot drift. The item's crumb only exists once the item has
  // loaded (`data-index/type/item/route.ts`), so until then it reads as the
  // list. One title for the whole page, outside the boundary, so the switch
  // from loading to loaded never passes through a title of neither.
  const crumbTitle = useBreadcrumbTitle();

  return (
    <Page>
      <DocumentTitle title={isEmptyString(crumbTitle) ? 'Data index' : crumbTitle} />
      <Suspense fallback={<LoadingPage />}>
        <DataIndexItemView address={address} />
      </Suspense>
    </Page>
  );
}

type DataIndexItemViewProps = {
  address: DataIndexItemAddress;
};

function DataIndexItemView({ address }: DataIndexItemViewProps) {
  const workspaceId = requireWorkspaceId();
  const item = useDataIndexItem(workspaceId, address);
  const tags = useItemTags(workspaceId, item.tagIds);
  const environment = useEnvironmentOptional();
  const objectTypes = useObjectTypes().data;
  const { hasPermission } = usePermissions();
  useItemViewTracking(address);

  const identifiers = environment.data?.environment?.identifiers;

  // The way back to the data index, which the page this replaces puts above
  // the header. Its crumb is the `data-index` route itself, whose redirect
  // opens the attributes list where it was left — from an event's page too —
  // so this is that list, remembered (`data-index/route.js`,
  // `data-index/index/route.ts:21`). An object's attribute is named for the
  // type it belongs to — "Companies.tier" — because the same name lives under
  // several types (`data-index/type/nested-item/route.ts:14-17`).
  useRegisterBreadcrumbs([
    {
      id: 'data-index',
      text: 'Data index',
      href: rememberedListPath(String(workspaceId), 'attributes'),
    },
    {
      id: 'data-index-item',
      text:
        address.scope.kind === 'profile'
          ? item.name
          : `${crumbObjectTypeName(objectTypes, address.scope)}.${item.name}`,
      href: dataIndexItemPath(String(workspaceId), address.kind, item.name, address.scope),
      // Names are the workspace's own and can run long; the crumb row does
      // not wrap.
      isTruncated: true,
    },
  ]);
  const model = useItemPageModel({
    address,
    item,
    objectTypes,
    identifiers,
    isEnvironmentLoading: environment.data == null,
    deletePermission: {
      granted: hasPermission('journeys/people/edit'),
      refusal: 'You do not have permission to delete attributes.',
    },
  });
  const reach = useItemReach(workspaceId, address, model.reach.withRow);

  return (
    <>
      <ItemHeader
        workspaceId={workspaceId}
        address={address}
        item={item}
        description={model.description}
        tags={tags}
        identifiers={identifiers}
        isMultiIdentifier={isMultiIdentifier({ identifier_type: environment.data?.environment?.identifier_type ?? '' })}
        canBeMarkedSensitive={model.canBeMarkedSensitive}
        deleteBlockReason={model.deleteBlockReason}
      />

      <PageBody>
        <Grid gap="300" w="full" columns="1fr 2fr">
          {/* Sized to its content rather than stretched down the row: on an
              event whose usage table is the taller column, a stretched card
              runs its border past its last row. The page this replaces says
              the same in one line (`data-index-item/index.css.ts:7-9`). */}
          <Panel alignSelf="start">
            <PanelHeader>
              <PanelTitle>Details</PanelTitle>
            </PanelHeader>
            <PanelSection>
              <ItemDetails
                workspaceId={workspaceId}
                address={address}
                item={item}
                tags={tags}
                reach={reach}
                model={model}
              />
            </PanelSection>
          </Panel>

          {/* A box between the grid and the tabs, so the tabs are sized by
              their content and not by the row. A grid item stretches to its
              row's height, and a stretched `Tabs` — a flex column whose tab
              list is `flex: 1` — hands that slack to the list rather than
              the panel, leaving the views row and the table floating 195px
              below the tabs on an item whose details run longer than its
              usage. The page this replaces wraps it the same way, and for
              the same reason (`data-index-item/index.gts:720-758`). Pluma's
              `Box` rather than a bare `div`: the grid item it replaces was
              one, and with it the reset's `min-width: 0` that keeps the
              track from growing past its share. */}
          <Box>
            <ItemPanels address={address} item={item} model={model} workspaceId={workspaceId} />
          </Box>
        </Grid>
      </PageBody>
    </>
  );
}

/**
 * The item's own view event, fired once per item rather than per navigation:
 * changing tab writes the URL and is the same view of the same item.
 */
function useItemViewTracking(address: DataIndexItemAddress) {
  const analytics = useAnalytics();
  const event = address.kind === 'attribute' ? 'dataIndexAttributeViewed' : 'dataIndexEventViewed';

  usePageViewTracking(
    () => {
      void analytics.track('logs', event, {});
    },
    true,
    { ignoreSearch: true },
  );
}

type ItemPanelsProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
  item: ReturnType<typeof useDataIndexItem>;
  model: ReturnType<typeof useItemPageModel>;
};

function ItemPanels({ workspaceId, address, item, model }: ItemPanelsProps) {
  const [tab, setTab] = useQueryState('tab');

  // Only an event has attributes of its own; whether it has any yet does not
  // decide the tab. The page this replaces gates it on the
  // `data_index_event_attribute` flag, which is on for every account, and
  // shows a zero badge for an event nothing has been sent for.
  const withEventAttributes = address.kind === 'event';
  // Held across renders, and replaced only when the item changes: the usage
  // tab's empty state keeps a create in flight, and the resolver hands over
  // a new address each render, which would remount it.
  const latestRef = itemRefFromAddress(address);
  const [itemRef, setItemRef] = useState(latestRef);
  if (!isSameItemRef(itemRef, latestRef)) {
    setItemRef(latestRef);
  }
  const withLiquidCaveat = showsLiquidCaveat(item.usage, model.usageScope);
  const activeTab = resolveItemTab(tab, withEventAttributes);

  return (
    <Tabs gap="200" activeTab={activeTab} onChange={(next) => void setTab(next)}>
      <TabsList>
        {itemTabs({ usage: model.usageCount, eventAttributes: item.eventAttributes.length }, withEventAttributes).map(
          (option) =>
            option.value === 'usage' && withLiquidCaveat ? (
              // A tooltip renders a button of its own, which may not sit
              // inside the tab's own: it is invalid, and it takes a tab
              // stop from inside a `role="tab"`. The page this replaces
              // switches the tab to a div the same way
              // (`data-index-item/index.gts:726`).
              <TabsTab key={option.value} value={option.value} badge={option.badge} as="div">
                <InlineStack alignBlock="center" gap="100">
                  {option.label}
                  <LiquidCaveat kind={address.kind} />
                </InlineStack>
              </TabsTab>
            ) : (
              <TabsTab key={option.value} value={option.value} badge={option.badge}>
                {option.label}
              </TabsTab>
            ),
        )}
      </TabsList>
      <TabsPanel value="usage">
        <UsageTab
          workspaceId={workspaceId}
          item={itemRef}
          itemName={item.name}
          usage={item.usage}
          scope={model.usageScope}
          terms={model.usageTerms}
        />
      </TabsPanel>
      <TabsPanel value="activity">
        <RecentActivity workspaceId={workspaceId} address={address} />
      </TabsPanel>
      {withEventAttributes ? (
        <TabsPanel value="attributes">
          <EventAttributesTable
            workspaceId={workspaceId}
            eventName={item.name}
            attributes={item.eventAttributes}
            // The plan alone, as Ember's `canEventAttributesBeMarkedSensitive`
            // has it, so a Viewer is offered the menu there too.
            canMarkSensitive={model.canRedact}
          />
        </TabsPanel>
      ) : null}
    </Tabs>
  );
}

/**
 * What an object's attribute is filed under in the crumb. "Object" rather
 * than the list's "Objects": this names one type, and the page this replaces
 * falls back to the singular (`data-index/type/nested-item/route.ts:14-17`).
 */
function crumbObjectTypeName(
  objectTypes: readonly { id: string; name: string }[] | undefined,
  scope: Exclude<DataIndexItemAddress['scope'], { kind: 'profile' }>,
): string {
  return (objectTypes ?? []).find((objectType) => Number(objectType.id) === scope.objectTypeId)?.name ?? 'Object';
}
